import React, { useState, useRef, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { KeyboardArrowDown } from '@mui/icons-material';
import { devLog, devWarn, devError } from '../../../utils/devLogger';
import GenericListUI, { FetchParams, FetchResult } from '../../../components/CustomList/GenericListUI';
import { ColumnDefinition } from '../../../components/CustomList/GenericDataTable';
import { getPaymentList, updatePayment } from '../../../lib/api/admin/adminApi';
import { PaymentListParams } from '../../../lib/api/admin/adminApi.types';
import { toast } from 'react-toastify';
import dayjs from 'dayjs';
import isBetween from 'dayjs/plugin/isBetween';
import CmsPopup from '../../../components/CmsPopup';
import { CmsPopupBtnType } from '../../../components/CmsPopupBtn';

// dayjs 플러그인 확장
dayjs.extend(isBetween);

// 결제 상세 정보 타입 정의
interface PaymentDetailData {
  artworkTitle: string;
  winnerName: string;
  winnerCountry: string;
  winnerEmail: string;
  winnerPhone: string;
  totalAmountKRW: number;
  totalAmountUSD: number;
  paymentStatus: string;
  refundStatus: string;
  memo: string;
}

// 결제 데이터 타입 정의 (실제 API 응답 구조에 맞게)
interface PaymentData {
  id?: number;
  no: number;
  index: number;
  user: {
    name: string;
    email: string;
    cellphone: string;
    locationType: string;
  };
  lot: {
    name: string;
    code: string | null;
    amount: number; // KRW 금액
    exchangeRate: number;
    usd: number; // USD 금액 (원 단위가 아닌 센트 단위로 보임)
  };
  isRefund: boolean;
  refundAmount: number | null;
  memo: string | null;
  createdAt: string;
  paidAt: string | null;
  status: string;
  // 계산된 필드들
  artworkTitle?: string; // lot.name에서 가져올 예정
  lotId?: string; // lot.code에서 가져올 예정
  totalAmountKRW?: number; // lot.amount에서 가져올 예정
  totalAmountUSD?: number; // lot.usd에서 가져올 예정 (센트를 달러로 변환)
  cancelAmountKRW?: number; // 계산된 값
  cancelAmountUSD?: number; // 계산된 값
  paymentStatus?: string; // status에서 가져올 예정
  winnerName?: string; // user.name에서 가져올 예정
  winnerCountry?: string; // user.locationType에서 가져올 예정
  winnerEmail?: string; // user.email에서 가져올 예정
  winnerPhone?: string; // user.cellphone에서 가져올 예정
  refundStatus?: string; // isRefund 기반으로 계산
  handlerName?: string; // 처리 담당자 (API 응답으로 추가 예정)
  handlerId?: string; // 담당자 ID (API 응답으로 추가 예정)
}

const PaymentManagementPage: React.FC = () => {
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const listRef = useRef<{ refetch: () => void }>(null);
  const [paymentSummary, setPaymentSummary] = useState({
    totalDepositAmount: 0,
    totalUnpaidAmount: 0,
    totalUnpaidCnt: 0
  });
  const [openDropdownId, setOpenDropdownId] = useState<number | null>(null);
  
  // 결제 상세 모달 상태
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
  const [selectedPaymentDetail, setSelectedPaymentDetail] = useState<PaymentDetailData | null>(null);
  const [selectedPaymentIndex, setSelectedPaymentIndex] = useState<number | null>(null); // API 업데이트용 index 저장
  const [paymentDetailForm, setPaymentDetailForm] = useState<PaymentDetailData>({
    artworkTitle: '',
    winnerName: '',
    winnerCountry: '',
    winnerEmail: '',
    winnerPhone: '',
    totalAmountKRW: 0,
    totalAmountUSD: 0,
    paymentStatus: 'PENDING',
    refundStatus: 'NONE',
    memo: '',
  });

  // 컴포넌트 마운트 시 로그
  React.useEffect(() => {
    devLog('PaymentManagement 컴포넌트 마운트됨');
  }, []);

  // 결제상태 업데이트 함수
  const updatePaymentStatus = async (paymentIndex: number, newStatus: string) => {
    try {
      devLog('결제상태 업데이트 요청:', { paymentIndex, newStatus });
      
      // API 호출하여 결제 상태 업데이트 (newStatus가 이미 PENDING 또는 PAID)
      await updatePayment(paymentIndex, { status: newStatus as "PENDING" | "PAID" | "CANCELLED" });
      
      toast.success('결제상태가 업데이트되었습니다.');
      
      // 리스트 새로고침
      if (listRef.current) {
        listRef.current.refetch();
      }
      setRefreshTrigger(prev => prev + 1);
      
    } catch (error) {
      devError('결제상태 업데이트 실패:', error);
      toast.error('결제상태 업데이트에 실패했습니다.');
    }
  };

  // 환불상태 업데이트 함수
  const updateRefundStatus = async (paymentIndex: number, newStatus: string, totalAmountKRW: number) => {
    try {
      devLog('환불상태 업데이트 요청:', { paymentIndex, newStatus, totalAmountKRW });
      
      // API 호출하여 환불 상태 업데이트
      const isRefund = newStatus === 'COMPLETED'; // 'COMPLETED' 값으로 체크
      const updateData = isRefund 
        ? { isRefund: true, refundAmount: totalAmountKRW } // 환불완료: 총 결제금액 KRW를 환불금액으로 설정
        : { isRefund: false, refundAmount: 0 }; // 공란: isRefund false, 환불금액 0
      
      await updatePayment(paymentIndex, updateData);
      
      toast.success('환불상태가 업데이트되었습니다.');
      
      // 리스트 새로고침
      if (listRef.current) {
        listRef.current.refetch();
      }
      setRefreshTrigger(prev => prev + 1);
      
    } catch (error) {
      devError('환불상태 업데이트 실패:', error);
      toast.error('환불상태 업데이트에 실패했습니다.');
    }
  };

  // 셀용 커스텀 드롭다운 컴포넌트
  const CellDropdown: React.FC<{
    value: string;
    onChange: (value: string) => void;
    options: Array<{ value: string; label: string }>;
    itemId: number;
    disabled?: boolean;
  }> = ({ value, onChange, options, itemId, disabled = false }) => {
    const dropdownRef = useRef<HTMLDivElement>(null);
    const [dropdownPosition, setDropdownPosition] = useState<{ top: number; left: number; width: number } | null>(null);
    const isOpen = openDropdownId === itemId;
    
    const selectedOption = options.find(opt => opt.value === value) || options[0];
    
    // 드롭다운 위치 계산
    useEffect(() => {
      if (isOpen && dropdownRef.current) {
        const rect = dropdownRef.current.getBoundingClientRect();
        setDropdownPosition({
          top: rect.bottom + window.scrollY + 4,
          left: rect.left + window.scrollX,
          width: rect.width
        });
      }
    }, [isOpen]);
    
    // 외부 클릭 감지
    useEffect(() => {
      const handleClickOutside = (event: MouseEvent) => {
        if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
          setOpenDropdownId(null);
        }
      };

      if (isOpen) {
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
      }
    }, [isOpen]);

    const handleToggle = (e: React.MouseEvent) => {
      if (disabled) return; // disabled 상태면 토글하지 않음
      e.stopPropagation();
      devLog('드롭다운 토글:', { isOpen, itemId });
      setOpenDropdownId(isOpen ? null : itemId);
    };

    const handleSelect = (optionValue: string) => {
      devLog('드롭다운 옵션 선택됨:', { optionValue, itemId });
      onChange(optionValue);
      setOpenDropdownId(null);
    };

    // 드롭다운 메뉴 컴포넌트
    const DropdownMenu = () => {
      if (!isOpen || !dropdownPosition) return null;

      return ReactDOM.createPortal(
        <div
          style={{
            position: 'absolute',
            top: dropdownPosition.top,
            left: dropdownPosition.left,
            width: dropdownPosition.width,
            backgroundColor: '#ffffff',
            border: '1px solid #e5e7eb',
            borderRadius: '12px',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
            zIndex: 99999,
            maxHeight: '200px',
            overflowY: 'auto',
            minWidth: '120px',
          }}
        >
          {options.map((option, index) => (
            <div key={option.value}>
              <div
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  devLog('드롭다운 옵션 클릭됨:', { optionValue: option.value, itemId });
                  handleSelect(option.value);
                }}
                onMouseDown={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                }}
                style={{
                  padding: '12px 16px',
                  fontSize: '14px',
                  color: '#374151',
                  backgroundColor: option.value === value ? 'rgba(59, 130, 246, 0.1)' : 'transparent',
                  cursor: 'pointer',
                  transition: 'background-color 0.15s ease-in-out',
                  borderTopLeftRadius: index === 0 ? '12px' : '0',
                  borderTopRightRadius: index === 0 ? '12px' : '0',
                  borderBottomLeftRadius: index === options.length - 1 ? '12px' : '0',
                  borderBottomRightRadius: index === options.length - 1 ? '12px' : '0',
                  userSelect: 'none',
                  WebkitUserSelect: 'none',
                  msUserSelect: 'none',
                  MozUserSelect: 'none',
                }}
                onMouseEnter={(e) => {
                  if (option.value !== value) {
                    (e.target as HTMLElement).style.backgroundColor = 'rgba(243, 244, 246, 0.8)';
                  }
                }}
                onMouseLeave={(e) => {
                  if (option.value !== value) {
                    (e.target as HTMLElement).style.backgroundColor = 'transparent';
                  }
                }}
              >
                <span>{option.label}</span>
                {option.value === value && (
                  <span style={{ float: 'right', color: '#3B82F6', fontWeight: '600' }}>✓</span>
                )}
              </div>
              {/* 구분선 (마지막 항목 제외) */}
              {index < options.length - 1 && (
                <div
                  style={{
                    width: '100%',
                    height: '1px',
                    backgroundColor: '#e5e7eb',
                  }}
                />
              )}
            </div>
          ))}
        </div>,
        document.body
      );
    };

    return (
      <div 
        ref={dropdownRef} 
        style={{ 
          position: 'relative', 
          width: '100%', 
          minWidth: '120px'
        }}
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
        }}
      >
        {/* 선택된 값 표시 (보더 없음) */}
        <div
          onClick={(e) => {
            if (disabled) return; // disabled 상태면 클릭 무시
            e.preventDefault();
            e.stopPropagation();
            handleToggle(e);
          }}
          onMouseDown={(e) => {
            if (disabled) return; // disabled 상태면 마우스 이벤트 무시
            e.preventDefault();
            e.stopPropagation();
          }}
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '8px 12px',
            cursor: disabled ? 'not-allowed' : 'pointer',
            backgroundColor: 'transparent',
            fontSize: '14px',
            color: disabled ? '#9CA3AF' : '#374151',
            fontWeight: '400',
            userSelect: 'none',
            WebkitUserSelect: 'none',
            msUserSelect: 'none',
            MozUserSelect: 'none',
            opacity: disabled ? 0.6 : 1,
          }}
        >
          <span>{selectedOption?.label}</span>
          <KeyboardArrowDown
            style={{
              color: disabled ? '#D1D5DB' : '#9CA3AF',
              transition: 'transform 0.2s ease-in-out',
              transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)',
              pointerEvents: 'none', // 아이콘은 클릭 이벤트 차단하지 않도록
            }}
          />
        </div>

        <DropdownMenu />
        
        {/* 배경 오버레이 */}
        {isOpen && (
          <>
            {ReactDOM.createPortal(
              <div
                style={{
                  position: 'fixed',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  zIndex: 99998,
                }}
                onClick={() => setOpenDropdownId(null)}
              />,
              document.body
            )}
          </>
        )}
      </div>
    );
  };

  // 결제상태 드롭다운 렌더러
  const renderPaymentStatusDropdown = (value: any, item: PaymentData) => {
    const currentStatus = item.paymentStatus || value || 'PENDING';
    
    const statusOptions = [
      { value: 'PENDING', label: '미입금' },
      { value: 'PAID', label: '입금완료' },
    ];

    return (
      <CellDropdown
        value={currentStatus}
        onChange={(newValue) => updatePaymentStatus(item.index, newValue)}
        options={statusOptions}
        itemId={item.no}
      />
    );
  };

  // 환불상태 드롭다운 렌더러
  const renderRefundStatusDropdown = (value: any, item: PaymentData) => {
    const currentStatus = item.refundStatus || value || 'NONE';
    const isPaymentCompleted = item.paymentStatus === 'PAID' || item.status === 'PAID'; // 결제상태가 입금완료인지 확인
    
    const refundOptions = [
      { value: 'NONE', label: '-' },
      { value: 'COMPLETED', label: '환불완료' },
    ];

    return (
      <CellDropdown
        value={currentStatus}
        onChange={(newValue) => updateRefundStatus(item.index, newValue, item.totalAmountKRW || 0)}
        options={refundOptions}
        itemId={item.no + 1000} // 다른 드롭다운과 구분하기 위해 offset 추가
        disabled={!isPaymentCompleted} // 결제상태가 입금완료가 아니면 비활성화
      />
    );
  };

  // 결제상태 포맷팅 함수
  const formatPaymentStatus = (status: string | undefined | null) => {
    if (!status) return '미입금';
    switch (status) {
      case 'PENDING':
        return '미입금';
      case 'PAID':
        return '입금완료';
      default:
        return status;
    }
  };

  // 환불상태 포맷팅 함수
  const formatRefundStatus = (status: string | undefined | null) => {
    if (!status) return '-';
    switch (status) {
      case 'NONE':
        return '-';
      case 'REQUESTED':
        return '환불요청';
      case 'PROCESSING':
        return '환부처리중';
      case 'COMPLETED':
        return '환부완료';
      case 'REJECTED':
        return '환부거절';
      default:
        return status;
    }
  };

  // 금액 포맷팅 함수
  const formatAmount = (amount: number | undefined | null, currency: string) => {
    if (amount === undefined || amount === null || isNaN(amount)) {
      return `0 ${currency}`;
    }
    return `${amount.toLocaleString()} ${currency}`;
  };

  // 컬럼 정의
  const columns: ColumnDefinition<PaymentData>[] = [
    {
      header: 'No',
      accessor: 'no',
      formatter: (value) => value?.toString() || '',
    },
    {
      header: '경매명',
      accessor: 'artworkTitle',
      formatter: (value) => value || '-',
    },
    {
      header: '총 결제(KRW)',
      accessor: 'totalAmountKRW' as keyof PaymentData,
      formatter: (value) => formatAmount(value as number, 'KRW'),
    },
    {
      header: '총 결제(USD)',
      accessor: 'totalAmountUSD' as keyof PaymentData,
      formatter: (value) => formatAmount(value as number, 'USD'),
    },
    {
      header: '취소(KRW)',
      accessor: 'cancelAmountKRW' as keyof PaymentData,
      formatter: (value) => formatAmount(value as number, 'KRW'),
    },
    {
      header: '취소(USD)',
      accessor: 'cancelAmountUSD' as keyof PaymentData,
      formatter: (value) => formatAmount(value as number, 'USD'),
    },
    {
      header: '결제상태',
      accessor: 'paymentStatus' as keyof PaymentData,
      formatter: (value, item) => renderPaymentStatusDropdown(value, item),
      excelFormatter: (value) => formatPaymentStatus(value as string), // Excel에서는 텍스트로 표시
    },
    {
      header: '낙찰자',
      accessor: 'winnerName',
      formatter: (value) => value || '-',
    },
    {
      header: '낙찰자 국가',
      accessor: 'winnerCountry',
      formatter: (value) => value || '-',
    },
    {
      header: '낙찰자 이메일',
      accessor: 'winnerEmail',
      formatter: (value) => value || '-',
    },
    {
      header: '낙찰자 연락처',
      accessor: 'winnerPhone',
      formatter: (value) => value || '-',
    },
    {
      header: '환불상태',
      accessor: 'refundStatus' as keyof PaymentData,
      formatter: (value, item) => renderRefundStatusDropdown(value, item),
      excelFormatter: (value) => formatRefundStatus(value as string), // Excel에서는 텍스트로 표시
    },
    {
      header: '처리 담당자',
      accessor: 'handlerName',
      formatter: (value) => value || '-',
    },
    {
      header: '담당자 ID',
      accessor: 'handlerId',
      formatter: (value) => value || '-',
    },
    {
      header: '메모',
      accessor: 'memo',
      formatter: (value) => value || '-',
    },
  ];

  // 실제 API 함수
  const fetchPaymentData = async (params: FetchParams): Promise<FetchResult<PaymentData>> => {
    try {
      devLog('fetchPaymentData API 호출:', params);
      
      // FetchParams를 PaymentListParams로 변환
      const apiParams: PaymentListParams = {
        keyword: params.keyword || undefined,
        fromDate: params.fromDate || undefined,
        toDate: params.toDate || undefined,
      };

      const response = await getPaymentList(apiParams);
      
      // 404 응답 또는 에러 응답인지 확인
      if (response.data?.statusCode === 404 || response.data?.error) {
        devLog('404 응답 또는 에러 응답 감지:', response.data);
        return {
          data: [],
          totalItems: 0,
          allItems: 0,
        };
      }
      
      // 실제 API 응답 구조에 맞게 처리
      // response.data = { statusCode, message, data: [...], metadata: {...} }
      let responseData = response.data?.data || response.data;
      
      // 배열이 아닌 경우 배열로 변환
      if (!Array.isArray(responseData)) {
        if (responseData?.items && Array.isArray(responseData.items)) {
          responseData = responseData.items;
        } else {
          // 404나 에러가 아닌 유효한 객체인 경우만 배열로 변환
          responseData = [];
        }
      }
      
      // 각 아이템에 id가 없는 경우 생성 및 필드 매핑
      responseData = responseData.map((item: any, index: number) => {
        // 취소금액 계산 (isRefund가 true일때 refundAmount 기준으로 계산)
        let cancelAmountKRW = 0;
        let cancelAmountUSD = 0;
        
        if (item.isRefund && item.refundAmount) {
          cancelAmountKRW = item.refundAmount; // 환불금액이 KRW 기준
          // USD 환불금액은 KRW/USD 비율로 계산 (totalKrw/totalUsd 비율 사용)
          if (item.totalKrw && item.totalUsd && item.totalKrw > 0) {
            cancelAmountUSD = (item.refundAmount * item.totalUsd) / item.totalKrw;
          }
        }

        // 국가 매핑
        const getCountryName = (locationType: string) => {
          switch (locationType) {
            case 'DOMESTIC':
              return '대한민국';
            case 'OVERSEAS':
            case 'INTERNATIONAL':
              return '해외';
            default:
              return locationType || '-';
          }
        };

        // 환불상태 계산
        const getRefundStatus = (isRefund: boolean) => {
          return isRefund ? 'COMPLETED' : 'NONE';
        };

        // 수수료 포함 총 결제금액 계산
        const calculateTotalAmountWithFees = (originalAmount: number, auctionFeeRate: number, platformFeeRate: number, isDomestic: boolean, isUSD: boolean = false) => {
          // 수수료율이 모두 0이면 원 금액 그대로
          if (auctionFeeRate === 0 && platformFeeRate === 0) {
            return originalAmount;
          }
          
          // 경매장 수수료 + 플랫폼 수수료 계산
          // USD는 반올림, KRW는 내림
          const auctionHouseFee = isUSD 
            ? Math.round(originalAmount * (auctionFeeRate / 100) * 100) / 100
            : Math.floor(originalAmount * (auctionFeeRate / 100));
          const platformFee = isUSD 
            ? Math.round(originalAmount * (platformFeeRate / 100) * 100) / 100
            : Math.floor(originalAmount * (platformFeeRate / 100));
          const totalFees = auctionHouseFee + platformFee;
          
          // 국내 사용자만 VAT 10% 추가
          const vatAmount = isDomestic ? (isUSD 
            ? Math.round(totalFees * 0.1 * 100) / 100
            : Math.floor(totalFees * 0.1)) : 0;
          
          return originalAmount + totalFees + vatAmount;
        };

        const isDomestic = item.user?.locationType === 'DOMESTIC';
        const calculatedKRW = calculateTotalAmountWithFees(
          item.totalKrw || 0, 
          item.auctionFeeRate || 0, 
          item.platformFeeRate || 0, 
          isDomestic,
          false // KRW
        );
        const calculatedUSD = calculateTotalAmountWithFees(
          item.totalUsd || 0, 
          item.auctionFeeRate || 0, 
          item.platformFeeRate || 0, 
          isDomestic,
          true // USD
        );
        
        return {
          ...item,
          id: item.id || item.no || index + 1,
          // 매핑된 필드들
          artworkTitle: item.auctionName || '-',
          totalAmountKRW: calculatedKRW,
          totalAmountUSD: Math.round(calculatedUSD * 100) / 100, // 소수점 2자리로 반올림
          cancelAmountKRW,
          cancelAmountUSD: Math.round(cancelAmountUSD * 100) / 100, // 소수점 2자리로 반올림
          paymentStatus: item.paymentStatus || 'UNKNOWN',
          winnerName: item.user?.name || '-',
          winnerCountry: getCountryName(item.user?.locationType),
          winnerEmail: item.user?.email || '-',
          winnerPhone: item.user?.cellphone || '-',
          refundStatus: getRefundStatus(item.isRefund),
          handlerName: item.updateBy?.name || item.updateBy || '-',
          handlerId: item.updateBy?.adminId || item.updateBy || '-',
          memo: item.memo || '-',
        };
      });
      
      // metadata에서 총액 정보 추출
      const metadata = response.data?.metadata || {};
      const totalDepositAmount = metadata.totalDepositAmount || 0;
      const totalUnpaidAmount = metadata.totalUnpaidAmount || 0;
      const totalUnpaidCnt = metadata.totalUnpaidCnt || 0;
      
      // 결제 요약 정보 업데이트
      setPaymentSummary({
        totalDepositAmount,
        totalUnpaidAmount,
        totalUnpaidCnt
      });
      
      // API 응답을 FetchResult 형태로 변환
      const result: FetchResult<PaymentData> = {
        data: responseData,
        totalItems: response.data?.metadata?.totalCnt || responseData.length,
        allItems: response.data?.metadata?.allCnt || responseData.length,
      };
      
      return result;
      
    } catch (error) {
      devError('결제 데이터 조회 실패:', error);
      toast.error('결제 리스트를 불러오는 중 오류가 발생했습니다.');
      
      // 에러 시 빈 결과 반환
      return {
        data: [],
        totalItems: 0,
        allItems: 0,
      };
    }
  };

  // 행 클릭 핸들러 (향후 상세 팝업 구현 시 사용)
  const handleRowClick = (item: PaymentData, index: number) => {
    devLog('결제 정보 클릭:', item);
    
    // PaymentData를 PaymentDetailData로 변환
    const detailData: PaymentDetailData = {
      artworkTitle: item.artworkTitle || '',
      winnerName: item.winnerName || '',
      winnerCountry: item.winnerCountry || '',
      winnerEmail: item.winnerEmail || '',
      winnerPhone: item.winnerPhone || '',
      totalAmountKRW: item.totalAmountKRW || 0,
      totalAmountUSD: item.totalAmountUSD || 0,
      paymentStatus: item.paymentStatus || 'PENDING',
      refundStatus: item.refundStatus || 'NONE',
      memo: (item.memo && item.memo !== '-') ? item.memo : '', // '-'인 경우 빈 문자열로 처리
    };
    
    setSelectedPaymentDetail(detailData);
    setSelectedPaymentIndex(item.index); // API 업데이트용 index 저장
    setPaymentDetailForm(detailData);
    setIsDetailModalOpen(true);
  };

  // 결제 상세 모달 닫기
  const handleCloseDetailModal = () => {
    setIsDetailModalOpen(false);
    setSelectedPaymentDetail(null);
    setSelectedPaymentIndex(null);
  };

  // 결제 상세 정보 저장
  const handleSavePaymentDetail = async () => {
    if (!selectedPaymentDetail || selectedPaymentIndex === null) return;
    
    try {
      devLog('결제 상세 정보 저장:', paymentDetailForm);
      
      // 실제 업데이트할 데이터만 추출
      const updateData: any = {};
      let hasChanges = false;
      
      // 결제상태 변경된 경우
      if (paymentDetailForm.paymentStatus !== selectedPaymentDetail.paymentStatus) {
        updateData.status = paymentDetailForm.paymentStatus as "PENDING" | "PAID" | "CANCELLED";
        hasChanges = true;
      }
      
      // 환불상태 변경된 경우
      if (paymentDetailForm.refundStatus !== selectedPaymentDetail.refundStatus) {
        const isRefund = paymentDetailForm.refundStatus === 'COMPLETED';
        updateData.isRefund = isRefund;
        updateData.refundAmount = isRefund ? paymentDetailForm.totalAmountKRW : 0;
        hasChanges = true;
      }
      
      // 메모 변경된 경우
      if (paymentDetailForm.memo !== selectedPaymentDetail.memo) {
        updateData.memo = paymentDetailForm.memo;
        hasChanges = true;
      }
      
      // 변경사항이 있는 경우만 API 호출
      if (hasChanges) {
        await updatePayment(selectedPaymentIndex, updateData);
        toast.success('결제 정보가 업데이트되었습니다.');
        
        // 리스트 새로고침
        if (listRef.current) {
          listRef.current.refetch();
        }
        setRefreshTrigger(prev => prev + 1);
      } else {
        toast.info('변경사항이 없습니다.');
      }
      
      handleCloseDetailModal();
      
    } catch (error) {
      devError('결제 상세 정보 저장 실패:', error);
      toast.error('결제 정보 업데이트에 실패했습니다.');
    }
  };

  // 결제 상세 폼 필드 업데이트
  const handleDetailFormChange = (field: keyof PaymentDetailData, value: string | number) => {
    setPaymentDetailForm(prev => {
      const newForm = {
        ...prev,
        [field]: value
      };
      
      // 결제상태가 미입금(PENDING)으로 변경되면 환불상태를 자동으로 NONE으로 설정
      if (field === 'paymentStatus' && value === 'PENDING') {
        newForm.refundStatus = 'NONE';
      }
      
      return newForm;
    });
  };

  // 결제 상세 계산 함수 (수수료 포함 상세 정보)
  const calculatePaymentDetails = (paymentData: PaymentDetailData) => {
    const isDomestic = paymentData.winnerCountry === '대한민국';
    const totalKRW = paymentData.totalAmountKRW || 0;
    const totalUSD = paymentData.totalAmountUSD || 0;
    
    // 역산으로 원 낙찰가 계산 (대략적 - 실제로는 API에서 상세 정보를 가져와야 함)
    // 기본 수수료율 가정
    const auctionFeeRate = 10; // 10%
    const platformFeeRate = 10; // 10%
    const vatRate = 10; // 10%
    
    // 총 수수료율
    const totalFeeRate = auctionFeeRate + platformFeeRate;
    const vatMultiplier = isDomestic ? (1 + (vatRate / 100)) : 1;
    
    // 역산으로 원 낙찰가 계산
    const originalBidKRW = Math.round(totalKRW / (1 + (totalFeeRate / 100) * vatMultiplier));
    const originalBidUSD = Math.round((totalUSD / (1 + (totalFeeRate / 100) * vatMultiplier)) * 100) / 100;
    
    // 수수료 계산
    const auctionFeeKRW = Math.floor(originalBidKRW * (auctionFeeRate / 100));
    const platformFeeKRW = Math.floor(originalBidKRW * (platformFeeRate / 100));
    const totalFeesKRW = auctionFeeKRW + platformFeeKRW;
    const vatKRW = isDomestic ? Math.floor(totalFeesKRW * (vatRate / 100)) : 0;
    
    const auctionFeeUSD = Math.round(originalBidUSD * (auctionFeeRate / 100) * 100) / 100;
    const platformFeeUSD = Math.round(originalBidUSD * (platformFeeRate / 100) * 100) / 100;
    const totalFeesUSD = auctionFeeUSD + platformFeeUSD;
    const vatUSD = isDomestic ? Math.round(totalFeesUSD * (vatRate / 100) * 100) / 100 : 0;
    
    return {
      originalBidKRW,
      originalBidUSD,
      auctionFeeKRW,
      platformFeeKRW,
      vatKRW,
      auctionFeeUSD,
      platformFeeUSD,
      vatUSD,
      auctionFeeRate,
      platformFeeRate,
      vatRate,
      isDomestic
    };
  };

  return (
    <>
      <GenericListUI<PaymentData>
        ref={listRef}
        title="결제 관리"
        columns={columns}
        fetchData={fetchPaymentData}
        excelFileName="PaymentList"
        totalAmountLabel=" "
        totalAmount={`입금완료: ${paymentSummary.totalDepositAmount.toLocaleString()} KRW | 미입금: ${paymentSummary.totalUnpaidAmount.toLocaleString()} KRW | 미입금 건수: ${paymentSummary.totalUnpaidCnt}건`}
        keyExtractor={(item, index) => {
          return item.id ? item.id.toString() : `payment-${index}`;
        }}
        enableSearch={true}
        searchPlaceholder="작품명, LOT ID, 낙찰자명, 이메일로 검색"
        enableDateFilter={true}
        dateRangeOptions={['금월', '지난달', '1년', '지정']}
        itemsPerPageOptions={[10, 20, 50, 100]}
        themeMode="light"
        onRowClick={handleRowClick}
        refreshTrigger={refreshTrigger}
      />

      {/* 결제 관리 상세 모달 */}
      <CmsPopup
        title="결제 관리 상세"
        isOpen={isDetailModalOpen}
        onClose={handleCloseDetailModal}
        isWide={false}
        rightButtons={[
          {
            label: '저장',
            onClick: handleSavePaymentDetail,
            type: CmsPopupBtnType.PRIMARY,
          },
          {
            label: '닫기',
            onClick: handleCloseDetailModal,
            type: CmsPopupBtnType.SECONDARY,
          }
        ]}
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
          {/* 작품정보 섹션 */}
          <div>
            <h3 style={{ 
              fontSize: '16px', 
              fontWeight: '600', 
              marginBottom: '16px', 
              color: '#333',
              borderBottom: '1px solid #e5e7eb',
              paddingBottom: '8px'
            }}>
              작품정보
            </h3>
            <div style={{ display: 'grid', gridTemplateColumns: '120px 1fr', gap: '12px', alignItems: 'center' }}>
              <label style={{ fontSize: '14px', fontWeight: '500', color: '#666' }}>작품명:</label>
              <div style={{ 
                padding: '8px 12px', 
                backgroundColor: '#f8f9fa', 
                borderRadius: '4px', 
                fontSize: '14px', 
                color: '#333' 
              }}>
                {paymentDetailForm.artworkTitle || '-'}
              </div>
            </div>
          </div>

          {/* 낙찰자정보 섹션 */}
          <div>
            <h3 style={{ 
              fontSize: '16px', 
              fontWeight: '600', 
              marginBottom: '16px', 
              color: '#333',
              borderBottom: '1px solid #e5e7eb',
              paddingBottom: '8px'
            }}>
              낙찰자정보
            </h3>
            <div style={{ display: 'grid', gridTemplateColumns: '120px 1fr', gap: '12px', alignItems: 'center' }}>
              <label style={{ fontSize: '14px', fontWeight: '500', color: '#666' }}>낙찰자:</label>
              <div style={{ 
                padding: '8px 12px', 
                backgroundColor: '#f8f9fa', 
                borderRadius: '4px', 
                fontSize: '14px', 
                color: '#333' 
              }}>
                {paymentDetailForm.winnerName || '-'}
              </div>
              
              <label style={{ fontSize: '14px', fontWeight: '500', color: '#666' }}>낙찰자 국가:</label>
              <div style={{ 
                padding: '8px 12px', 
                backgroundColor: '#f8f9fa', 
                borderRadius: '4px', 
                fontSize: '14px', 
                color: '#333' 
              }}>
                {paymentDetailForm.winnerCountry || '-'}
              </div>
              
              <label style={{ fontSize: '14px', fontWeight: '500', color: '#666' }}>낙찰자 이메일:</label>
              <div style={{ 
                padding: '8px 12px', 
                backgroundColor: '#f8f9fa', 
                borderRadius: '4px', 
                fontSize: '14px', 
                color: '#333' 
              }}>
                {paymentDetailForm.winnerEmail || '-'}
              </div>
              
              <label style={{ fontSize: '14px', fontWeight: '500', color: '#666' }}>낙찰자 연락처:</label>
              <div style={{ 
                padding: '8px 12px', 
                backgroundColor: '#f8f9fa', 
                borderRadius: '4px', 
                fontSize: '14px', 
                color: '#333' 
              }}>
                {paymentDetailForm.winnerPhone || '-'}
              </div>
            </div>
          </div>

          {/* 결제 정보 섹션 */}
          <div>
            <h3 style={{ 
              fontSize: '16px', 
              fontWeight: '600', 
              marginBottom: '16px', 
              color: '#333',
              borderBottom: '1px solid #e5e7eb',
              paddingBottom: '8px'
            }}>
              결제 정보
            </h3>
            {(() => {
              const details = calculatePaymentDetails(paymentDetailForm);
              return (
                <div style={{ display: 'grid', gridTemplateColumns: '150px 1fr', gap: '12px', alignItems: 'center' }}>
                  <label style={{ fontSize: '14px', fontWeight: '500', color: '#666' }}>낙찰가:</label>
                  <div style={{ 
                    padding: '8px 12px', 
                    backgroundColor: '#f8f9fa', 
                    borderRadius: '4px', 
                    fontSize: '14px', 
                    color: '#333',
                    display: 'flex',
                    justifyContent: 'space-between'
                  }}>
                    <span>{details.originalBidKRW?.toLocaleString() || '0'}원</span>
                    <span style={{ color: '#888' }}>(${details.originalBidUSD?.toFixed(2) || '0.00'})</span>
                  </div>
                  
                  <label style={{ fontSize: '14px', fontWeight: '500', color: '#666' }}>경매장 수수료 ({details.auctionFeeRate}%):</label>
                  <div style={{ 
                    padding: '8px 12px', 
                    backgroundColor: '#f8f9fa', 
                    borderRadius: '4px', 
                    fontSize: '14px', 
                    color: '#333',
                    display: 'flex',
                    justifyContent: 'space-between'
                  }}>
                    <span>{details.auctionFeeKRW?.toLocaleString() || '0'}원</span>
                    <span style={{ color: '#888' }}>(${details.auctionFeeUSD?.toFixed(2) || '0.00'})</span>
                  </div>
                  
                  <label style={{ fontSize: '14px', fontWeight: '500', color: '#666' }}>온라인 플랫폼 수수료 ({details.platformFeeRate}%):</label>
                  <div style={{ 
                    padding: '8px 12px', 
                    backgroundColor: '#f8f9fa', 
                    borderRadius: '4px', 
                    fontSize: '14px', 
                    color: '#333',
                    display: 'flex',
                    justifyContent: 'space-between'
                  }}>
                    <span>{details.platformFeeKRW?.toLocaleString() || '0'}</span>
                    <span style={{ color: '#888' }}>(${details.platformFeeUSD?.toFixed(2) || '0.00'})</span>
                  </div>
                  
                  {details.isDomestic && (
                    <>
                      <label style={{ fontSize: '14px', fontWeight: '500', color: '#666' }}>경매장 수수료 부가세 ({details.vatRate}%):</label>
                      <div style={{ 
                        padding: '8px 12px', 
                        backgroundColor: '#f8f9fa', 
                        borderRadius: '4px', 
                        fontSize: '14px', 
                        color: '#333',
                        display: 'flex',
                        justifyContent: 'space-between'
                      }}>
                        <span>{details.vatKRW?.toLocaleString() || '0'}</span>
                        <span style={{ color: '#888' }}>(${details.vatUSD?.toFixed(2) || '0.00'})</span>
                      </div>
                    </>
                  )}
                  
                  <div style={{ gridColumn: '1 / -1', height: '1px', backgroundColor: '#e5e7eb', margin: '8px 0' }}></div>
                  
                  <label style={{ fontSize: '14px', fontWeight: '600', color: '#333' }}>총 결제 금액:</label>
                  <div style={{ 
                    padding: '8px 12px', 
                    backgroundColor: '#e3f2fd', 
                    borderRadius: '4px', 
                    fontSize: '14px', 
                    color: '#333',
                    fontWeight: '600',
                    display: 'flex',
                    justifyContent: 'space-between'
                  }}>
                    <span>{paymentDetailForm.totalAmountKRW?.toLocaleString() || '0'} KRW</span>
                    <span style={{ color: '#666' }}>(${paymentDetailForm.totalAmountUSD?.toFixed(2) || '0.00'} USD)</span>
                  </div>
                  
                  <label style={{ fontSize: '14px', fontWeight: '500', color: '#666' }}>결제 상세:</label>
                  <div style={{ minHeight: '36px', display: 'flex', alignItems: 'center' }}>
                    <CellDropdown
                      value={paymentDetailForm.paymentStatus}
                      onChange={(newValue) => handleDetailFormChange('paymentStatus', newValue)}
                      options={[
                        { value: 'PENDING', label: '미입금' },
                        { value: 'PAID', label: '입금완료' },
                      ]}
                      itemId={999999} // 모달용 고유 ID
                    />
                  </div>
                  
                  <label style={{ fontSize: '14px', fontWeight: '500', color: '#666' }}>환불처리상태:</label>
                  <div style={{ minHeight: '36px', display: 'flex', alignItems: 'center' }}>
                    <CellDropdown
                      value={paymentDetailForm.refundStatus}
                      onChange={(newValue) => handleDetailFormChange('refundStatus', newValue)}
                      options={[
                        { value: 'NONE', label: '-' },
                        { value: 'COMPLETED', label: '환불완료' },
                      ]}
                      itemId={999998} // 모달용 고유 ID
                      disabled={paymentDetailForm.paymentStatus === 'PENDING'} // 미입금일 때 비활성화
                    />
                  </div>
                </div>
              );
            })()}
          </div>

          {/* 메모 섹션 */}
          <div>
            <label style={{ 
              display: 'block',
              fontSize: '14px', 
              fontWeight: '500', 
              color: '#666',
              marginBottom: '8px'
            }}>
              메모
            </label>
            <textarea
              value={paymentDetailForm.memo}
              onChange={(e) => handleDetailFormChange('memo', e.target.value)}
              rows={4}
              style={{
                width: '100%',
                padding: '12px',
                border: '1px solid #ddd',
                borderRadius: '4px',
                fontSize: '14px',
                resize: 'vertical',
                minHeight: '100px',
                boxSizing: 'border-box',
                outline: 'none',
                fontFamily: 'inherit',
                lineHeight: '1.5'
              }}
              placeholder="메모를 입력하세요..."
            />
          </div>
        </div>
      </CmsPopup>
    </>
  );
};

export default PaymentManagementPage;
